import { Key } from "react";

import { queryCache } from "react-query";

import { VERO1_QUERY_KEYS } from "manager/hooks/api/vero1Queries";
import { createAutosaveEngine, AutosaveEngine } from "shared/autosave";
import type {
  Vero1Recommendation,
  Vero1CancelReason,
} from "shared/interfaces/api/vero1";
import { editVero1ChecklistReport } from "shared/lib/api/vero1Api";

import { codeOf, Vero1ConflictCode } from "./persistItem";
import {
  emitAutosaveOutcome,
  Vero1AutosaveTerminal,
  Vero1OutcomeCode,
} from "./vero1Telemetry";

const REPORTED_OUTCOMES: ReadonlySet<string> = new Set(["blocked", "failed"]);

/** The recommendation step's draft-level fields, staged in FE shape. */
export interface Vero1ReportFields {
  finalRecommendation?: Vero1Recommendation | null;
  finalRecommendationLabel?: string;
  cancelReason?: Vero1CancelReason | "";
  instructionLine?: string;
  decisionRationale?: string;
  clientFindings?: string;
  /** Copy over's source version — sent once with the copied values so the
   *  server records the copy; never folded into the cached checklist. */
  copiedFromVersion?: number;
  [field: string]: unknown;
}

export interface Vero1ReportAutosaveQueue {
  engine: AutosaveEngine<Vero1ReportFields>;
  /** Re-pointed each render — the queue outlives any single mount. */
  dealIdRef: { current: Key | null };
}

const queues = new Map<string, Vero1ReportAutosaveQueue>();

export const REPORT_ENTITY = "report";

export const getVero1ReportAutosaveQueue = (
  checklistId: Key
): Vero1ReportAutosaveQueue => {
  const key = String(checklistId);
  const existing = queues.get(key);
  if (existing) return existing;

  const dealIdRef: Vero1ReportAutosaveQueue["dealIdRef"] = { current: null };
  let lastCode: Vero1OutcomeCode | null = null;

  const engine = createAutosaveEngine<Vero1ReportFields>({
    explicitFields: [],
    onEvent: (event) => {
      if (event.type !== "entity" || !REPORTED_OUTCOMES.has(event.status)) {
        return;
      }
      emitAutosaveOutcome({
        outcome: event.status as Vero1AutosaveTerminal,
        conflictCode: lastCode,
        queue: "report",
        dealId: dealIdRef.current ?? "",
        checklistId: key,
      });
    },
    // last-write-wins between analysts (the drafts concurrency model) — no
    // token gate: a teammate's newer prose should never 409 this keystroke
    commit: async (_entityId, fields) => {
      const dealId = dealIdRef.current;
      if (dealId === null) return { status: "failed" };
      try {
        await editVero1ChecklistReport(dealId, {
          ...("finalRecommendation" in fields
            ? {
                final_recommendation: (fields.finalRecommendation ?? null) as
                  | string
                  | null,
              }
            : {}),
          // the client's word for the pick rides with it: the step stages it
          // like any other field, so dropping it here loses the choice on any
          // navigation away before submit
          ...("finalRecommendationLabel" in fields
            ? {
                final_recommendation_label: (fields.finalRecommendationLabel ??
                  "") as string,
              }
            : {}),
          ...("cancelReason" in fields
            ? { cancel_reason: (fields.cancelReason ?? "") as string }
            : {}),
          ...("instructionLine" in fields
            ? { instruction_line: fields.instructionLine as string }
            : {}),
          ...("decisionRationale" in fields
            ? { decision_rationale: fields.decisionRationale as string }
            : {}),
          ...("clientFindings" in fields
            ? { client_findings: fields.clientFindings as string }
            : {}),
          ...(typeof fields.copiedFromVersion === "number"
            ? { copied_from_version: fields.copiedFromVersion }
            : {}),
        });
        // the slim response carries no checklist — fold the saved fields into
        // the cached payload so a re-mounted step restores what was written
        queryCache.setQueryData(
          VERO1_QUERY_KEYS.getVero1Checklist(dealId),
          (prev: any) =>
            prev?.checklist
              ? {
                  ...prev,
                  checklist: {
                    ...prev.checklist,
                    ...("finalRecommendation" in fields
                      ? {
                          finalRecommendation:
                            fields.finalRecommendation ?? null,
                        }
                      : {}),
                    ...("finalRecommendationLabel" in fields
                      ? {
                          finalRecommendationLabel:
                            fields.finalRecommendationLabel ?? "",
                        }
                      : {}),
                    ...("cancelReason" in fields
                      ? { cancelReason: fields.cancelReason ?? "" }
                      : {}),
                    ...("instructionLine" in fields
                      ? { instructionLine: fields.instructionLine }
                      : {}),
                    ...("decisionRationale" in fields
                      ? { decisionRationale: fields.decisionRationale }
                      : {}),
                    ...("clientFindings" in fields
                      ? { clientFindings: fields.clientFindings }
                      : {}),
                  },
                }
              : prev
        );
        lastCode = null;
        return { status: "saved" };
      } catch (err: any) {
        const status = err?.statusCode ?? err?.response?.status;
        if (status === 404) {
          lastCode = Vero1ConflictCode.NotADraft;
          return { status: "blocked" };
        }
        if (status === 409) {
          lastCode = codeOf(err) ?? Vero1ConflictCode.NotADraft;
          return { status: "blocked" };
        }
        // a refused copy source must not ride every later save into the same
        // 400 — retire it and let the copied values save on their own
        if (status === 400 && typeof fields.copiedFromVersion === "number") {
          lastCode = null;
          return { status: "rejected", dropFields: ["copiedFromVersion"] };
        }
        lastCode = null;
        return { status: "failed" };
      }
    },
  });
  // the commit ignores tokens — seed one so the engine never withholds saves
  engine.setToken("last-write-wins");

  const handle = { engine, dealIdRef };
  queues.set(key, handle);
  return handle;
};

/** Submitted or discarded — pending report edits have nothing to land on. */
export const disposeVero1ReportAutosaveQueue = (checklistId: Key) => {
  const handle = queues.get(String(checklistId));
  if (!handle) return;
  handle.engine.dropAll();
  handle.engine.stop();
  queues.delete(String(checklistId));
};
